import type { AppCategory } from '@bliks-io/contracts';
import { BarChart3, FileInput, Target, type LucideIcon } from 'lucide-react';
import type { NavMainItem, NavSubItem } from './NavMain';
import { BETA_APP_IDS } from './navigationConfig';
import { CATEGORY_LABEL_KEYS, resolveAppLabel, type InstalledAppEntry } from '@/lib/app-registry';
import { resolveAppTitle } from '@/lib/app-loader';
import { resolveAppIcon } from '@/lib/app-icon-resolver';

const CATEGORY_ICONS = {
  analysis: BarChart3,
  'data-capture': FileInput,
  sales: Target
} as const satisfies Record<AppCategory, LucideIcon>;

/**
 * Where a manifest route leads: `/apps/<path>`. The path is the app id for
 * an app with one page, `<appId>/<view>` for a view of an app with several
 * (SAL-16); an empty path means the app itself.
 */
export function appRouteUrl(appId: string, path: string): string {
  const trimmed = path.replace(/^\/+|\/+$/g, '');
  return `/apps/${trimmed || appId}`;
}

/**
 * Apps carrying a known category render inside that category's dropdown; every
 * other app stays a flat sidebar entry.
 *
 * An app with one route is named after the app; an app with several routes
 * lists its views, each named after its route, so the sidebar's second
 * level is the app's pages — as it is for the core's own sections.
 *
 * Omniscient is pinned flat by appId: its manifest category is `analysis`, but
 * it is the workspace entry point rather than one analysis app among others.
 * Nothing re-parses the manifest on read either, so a category value this build
 * has never heard of also falls back to flat instead of vanishing.
 */
export function buildAppNavItems(
  apps: InstalledAppEntry[] | undefined,
  language: string
): NavMainItem[] {
  const flatItems: NavMainItem[] = [];
  const buckets = new Map<AppCategory, NavSubItem[]>();

  for (const app of apps ?? []) {
    const category = app.category;
    const grouped =
      app.appId !== 'omniscient' &&
      category !== undefined &&
      Object.prototype.hasOwnProperty.call(CATEGORY_LABEL_KEYS, category);
    const manyViews = app.routes.length > 1;

    for (const route of app.routes) {
      const item = {
        title: manyViews
          ? resolveAppLabel(route.label, language)
          : resolveAppTitle(app.appId, route.label, language),
        url: appRouteUrl(app.appId, route.path),
        icon: resolveAppIcon(route.icon),
        beta: BETA_APP_IDS.has(app.appId)
      };
      if (grouped) {
        const bucket = buckets.get(category) ?? [];
        bucket.push(item);
        buckets.set(category, bucket);
      } else {
        flatItems.push(item);
      }
    }
  }

  const groups = (Object.keys(CATEGORY_LABEL_KEYS) as AppCategory[]).flatMap((category) => {
    const items = buckets.get(category);
    if (!items?.length) return [];
    return [{ title: CATEGORY_LABEL_KEYS[category], icon: CATEGORY_ICONS[category], items }];
  });

  return [...flatItems, ...groups];
}
